import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { trackEvent } from '../../utils/analytics';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SocialSignIn } from '../../components/auth/social-sign-in';

/**
 * Standalone sign-in for the gym-admin subdomain. Uses the same Devise token
 * API as the main app (via useAuth). Once authenticated the app re-renders into
 * the sidebar shell, so there is nothing to navigate to here.
 */
export default function GymSignIn() {
  const { t } = useTranslation();
  const { signIn, signInWithProvider } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);
    setFormError(null);
    try {
      const result = await signIn(email, password);
      if (result.success) {
        trackEvent('sign_in');
      } else if (result.error) {
        setFormError(result.error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    setFormError(null);
    const result = await signInWithProvider('google', credential);
    if (result.success) {
      trackEvent('sign_in');
    } else if (result.error) {
      setFormError(result.error);
    }
  };

  return (
    <div className="flex min-h-svh items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('gymAdmin.signIn.title')}</CardTitle>
          <CardDescription>{t('gymAdmin.signIn.description')}</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <SocialSignIn onGoogleCredential={handleGoogleCredential} googleText="signin_with" />
            {formError && (
              <div className="p-3 rounded-md bg-destructive/15 text-destructive text-sm">{formError}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
